/**
 * Platform abstraction layer for React Native compatibility.
 *
 * This module provides interfaces and default (web) implementations for
 * browser-specific APIs that are unavailable in React Native:
 *   - Storage        (localStorage / sessionStorage)
 *   - TextEncoder
 *   - Base-64        (btoa / atob)
 *   - SubtleCrypto   (crypto.subtle.digest)
 *   - DOM helpers    (document.*, requestAnimationFrame)
 *   - Location       (window.location.origin)
 *   - UserAgent      (navigator.userAgent)
 *
 * React Native consumers call `setPlatform(adapter)` once at startup to
 * supply their own implementations (e.g. AsyncStorage-backed storage,
 * expo-crypto, react-native-get-random-values, etc.).
 *
 * Web consumers don't need to do anything — the defaults use the browser APIs.
 */
/** In-memory fallback when neither browser storage nor a custom adapter exist. */
class MemoryStorage {
    constructor() {
        this.store = new Map();
    }
    getItem(key) { var _a; return (_a = this.store.get(key)) !== null && _a !== void 0 ? _a : null; }
    setItem(key, value) { this.store.set(key, value); }
    removeItem(key) { this.store.delete(key); }
}
// ---------------------------------------------------------------------------
// Default web implementation
// ---------------------------------------------------------------------------
function createWebPlatform() {
    const hasWindow = typeof window !== 'undefined';
    const hasDoc = typeof document !== 'undefined';
    return {
        storage: hasWindow && typeof localStorage !== 'undefined'
            ? localStorage
            : new MemoryStorage(),
        sessionStorage: hasWindow && typeof sessionStorage !== 'undefined'
            ? sessionStorage
            : new MemoryStorage(),
        textEncode(input) {
            return new TextEncoder().encode(input);
        },
        atob(input) {
            if (hasWindow)
                return window.atob(input);
            // Node / SSR fallback
            return Buffer.from(input, 'base64').toString('binary');
        },
        btoa(input) {
            if (hasWindow)
                return window.btoa(input);
            return Buffer.from(input, 'binary').toString('base64');
        },
        async sha256(data) {
            if (typeof crypto !== 'undefined' && crypto.subtle) {
                return crypto.subtle.digest('SHA-256', data);
            }
            // Node / SSR fallback
            const { createHash } = await import('crypto');
            const hash = createHash('sha256').update(data).digest();
            return hash.buffer.slice(hash.byteOffset, hash.byteOffset + hash.byteLength);
        },
        getUserAgent() {
            if (hasWindow && typeof navigator !== 'undefined')
                return navigator.userAgent;
            return '';
        },
        getLocationOrigin() {
            if (hasWindow && window.location)
                return window.location.origin;
            return undefined;
        },
        hasDOM: hasDoc,
    };
}
// ---------------------------------------------------------------------------
// Singleton & public API
// ---------------------------------------------------------------------------
let _platform = createWebPlatform();
let _platformConfigured = false;
/**
 * Override the platform adapter (call once at app startup in React Native).
 *
 * Must be called **before** `init()`. Can only be called once — subsequent
 * calls throw to prevent accidental mid-session storage swaps.
 *
 * ```ts
 * import { setPlatform } from '@pooflabs/web';
 * import AsyncStorage from '@react-native-async-storage/async-storage';
 *
 * // Create a synchronous wrapper around AsyncStorage for the adapter
 * // (see docs for a full example).
 * setPlatform({ storage: myStorage, ... });
 * ```
 */
export function setPlatform(adapter) {
    if (_platformConfigured) {
        throw new Error('setPlatform() has already been called. It can only be invoked once at startup.');
    }
    _platformConfigured = true;
    _platform = Object.assign(Object.assign({}, _platform), adapter);
}
/** Get the current platform adapter. */
export function getPlatform() {
    return _platform;
}
// ---------------------------------------------------------------------------
// Mobile / Android detection helpers
// ---------------------------------------------------------------------------
/**
 * Detect mobile device even when Chrome "Request Desktop Site" is active.
 *
 * Desktop mode on Android strips "Android" and "Mobile" from the UA, leaving
 * something like "Linux x86_64 …". The fallback detects this by looking for
 * touch capability + small viewport + a UA that doesn't belong to a known
 * desktop OS (ChromeOS, Windows, macOS).
 */
export function detectMobile() {
    const ua = _platform.getUserAgent();
    if (/Android|iPhone|iPad|iPod/i.test(ua))
        return true;
    if (typeof navigator !== 'undefined' && typeof window !== 'undefined') {
        const hasTouch = navigator.maxTouchPoints > 0;
        // Macs don't have touch screens, so "Macintosh" + touch = iOS desktop mode
        // (also catches iPadOS 13+ which always reports as Macintosh)
        if (/Macintosh/i.test(ua) && hasTouch)
            return true;
        // Android desktop mode: Linux UA + touch + small viewport, excluding
        // known desktop OSes (ChromeOS, Windows, macOS)
        const isSmallViewport = window.innerWidth <= 1024;
        const isDesktopOS = /CrOS|Windows|Macintosh/i.test(ua);
        if (hasTouch && isSmallViewport && !isDesktopOS)
            return true;
    }
    return false;
}
/**
 * Detect Android, including desktop-mode Chrome and Seeker/Saga in-app browsers.
 */
export function detectAndroid() {
    const ua = _platform.getUserAgent();
    if (/Android/i.test(ua))
        return true;
    if (/SolanaWallet|SeedVault/i.test(ua))
        return true;
    // Desktop-mode fallback: detected as mobile + Linux UA + not another OS
    if (detectMobile() && /Linux/i.test(ua) && !/CrOS|Macintosh|iPhone|iPad/i.test(ua))
        return true;
    return false;
}
/**
 * Reset the platform to web defaults and clear the configured flag.
 * **For testing only** — not exported from the public API.
 * @internal
 */
export function _resetPlatformForTesting() {
    _platform = createWebPlatform();
    _platformConfigured = false;
}
